import { useState, useEffect, useCallback, useRef } from 'react';
import { generateProblem } from '../utils/gameUtils';

const INITIAL_TIME = 10;
const TIME_BONUS = 2;
const INITIAL_RANGE = { min: 0, max: 5 };

export const useGame = (onGameOver) => {
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(INITIAL_TIME);
    const [range, setRange] = useState(INITIAL_RANGE);
    const [currentProblem, setCurrentProblem] = useState(() => generateProblem(INITIAL_RANGE));
    const [status, setStatus] = useState('idle');
    
    // Animation states for feedback
    const [feedback, setFeedback] = useState('none');

    const timerRef = useRef(null);

    const startGame = useCallback(() => {
        setScore(0);
        setTimeLeft(INITIAL_TIME);
        setRange(INITIAL_RANGE);
        setCurrentProblem(generateProblem(INITIAL_RANGE));
        setStatus('playing');
        setFeedback('none');
    }, []);

    const stopGame = useCallback(() => {
        setStatus('ended');
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    }, []);

    useEffect(() => {
        if (status === 'playing') {
            timerRef.current = window.setInterval(() => {
                setTimeLeft((prev) => {
                    if (prev <= 0.1) {
                        stopGame();
                        return 0;
                    }
                    return prev - 0.1;
                });
            }, 100);
        }
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [status, stopGame]);

    // Trigger game over callback when state actually updates to ended
    useEffect(() => {
        if (status === 'ended' && timeLeft <= 0) {
            onGameOver({ score, timeLeft: 0, range, currentProblem });
        }
    }, [status, timeLeft, score, range, currentProblem, onGameOver]);

    const submitAnswer = useCallback((userAnswer) => {
        if (status !== 'playing') return;

        if (userAnswer === currentProblem.answer) {
            // Correct
            setScore((s) => s + 1);
            setTimeLeft((t) => t + TIME_BONUS);
            setRange((r) => ({ min: r.min + 2, max: r.max + 4 }));
            setFeedback('correct');
            
            // Generate new problem with UPDATED range logic locally to avoid wait
            const nextRange = { min: range.min + 2, max: range.max + 4 };
            setCurrentProblem(generateProblem(nextRange));
        } else {
            // Wrong
            setScore((s) => Math.max(0, s - 1));
            setFeedback('wrong');
        }

        // Reset feedback animation
        setTimeout(() => setFeedback('none'), 300);
    }, [status, currentProblem, range]);

    return {
        score,
        timeLeft,
        currentProblem,
        feedback,
        startGame,
        submitAnswer
    };
};

